/**
 * Plan urządzenia fotowoltaicznego dla ekip ratowniczych (etap B narzędzia PSP, wersja uproszczona).
 *
 * PO CO: art. 29 ust. 4 pkt 3 lit. c Prawa budowlanego wymaga przekazania planu razem z zawiadomieniem do PSP.
 * Wzór Środy Śląskiej (`VV/dane_badawcze/psp/README.md`) wylicza, co plan pokazuje: obszar modułów, trasy DC i AC,
 * osłony tras, falowniki, element odłączający stronę DC, legendę, autora i datę.
 *
 * ZASADY (decyzja właściciela 2026-10-09): nie znamy rzutu domu, więc rysujemy schematyczny rzut z góry z wyborów
 * użytkownika (strony świata), bez wymiarów i bez skali. Góra rysunku to północ. Nie oceniamy zgodności.
 *
 * `opisPlanu` zwraca geometrię w umownych jednostkach (pole RYS) i teksty; rysują ją `pdfPlanuRatowniczego.js`
 * (PDF) i podgląd SVG w formularzu, więc oba widoki są takie same.
 */
import { liczbaPl } from './zgloszenieOsd'
import {
  PUSTY_STAN, ZDANIE_DC_POD_NAPIECIEM, zdanieOdlaczenia, zdanieZasilaniaRezerwowego, dataPl, NAZWY_POMIESZCZEN, polozenieFalownika, polozenieMagazynu,
} from './zawiadomieniePsp'

export const RYS = { w: 160, h: 120 }

export const STRONY = [['N', 'północna'], ['E', 'wschodnia'], ['S', 'południowa'], ['W', 'zachodnia']]
export const POLACIE = [['S', 'południowa'], ['W', 'zachodnia'], ['E', 'wschodnia'], ['N', 'północna'], ['EW', 'wschodnia i zachodnia']]
export const TRASY_DC = [['wewnatrz', 'wewnątrz budynku'], ['elewacja', 'po zewnętrznej ścianie']]

const BUDYNEK = { x: 35, y: 25, w: 90, h: 70 }
/** Instalacja na gruncie: budynek i pole modułów obok siebie, pole po wybranej stronie budynku. */
const UKLAD_GRUNTU = {
  E: { b: { x: 18, y: 24, w: 62, h: 72 }, p: { x: 114, y: 20, w: 36, h: 80 } },
  W: { b: { x: 80, y: 24, w: 62, h: 72 }, p: { x: 10, y: 20, w: 36, h: 80 } },
  S: { b: { x: 40, y: 16, w: 80, h: 54 }, p: { x: 25, y: 98, w: 110, h: 16 } },
  N: { b: { x: 40, y: 50, w: 80, h: 54 }, p: { x: 25, y: 6, w: 110, h: 16 } },
}
const PRZECIWNA = { N: 'S', S: 'N', E: 'W', W: 'E' }
const ODSUNIECIE_WEW = -9
const ODSUNIECIE_ZEW = 12

/** Punkt przy ścianie: `odsuniecie` > 0 na zewnątrz, < 0 do środka; `t` 0..1 wzdłuż ściany. */
export const naScianie = (r, strona, odsuniecie, t) => {
  switch (strona) {
    case 'N': return { x: r.x + r.w * t, y: r.y - odsuniecie }
    case 'S': return { x: r.x + r.w * t, y: r.y + r.h + odsuniecie }
    case 'W': return { x: r.x - odsuniecie, y: r.y + r.h * t }
    default: return { x: r.x + r.w + odsuniecie, y: r.y + r.h * t }
  }
}

/** Pola modułów na dachu albo fasadzie (prostokąty) i linia kalenicy dla dachu skośnego. */
const polaNaBudynku = (b, montaz, strona) => {
  const cx = b.x + b.w / 2
  const cy = b.y + b.h / 2
  if (montaz === 'dach_plaski') return { pola: [{ x: b.x + 10, y: b.y + 10, w: b.w - 20, h: b.h - 20 }], kalenica: null }
  if (montaz === 'fasada') {
    const s = strona === 'EW' ? 'S' : strona
    const pas = { N: { x: b.x + 10, y: b.y - 7, w: b.w - 20, h: 5 }, S: { x: b.x + 10, y: b.y + b.h + 2, w: b.w - 20, h: 5 },
      W: { x: b.x - 7, y: b.y + 10, w: 5, h: b.h - 20 }, E: { x: b.x + b.w + 2, y: b.y + 10, w: 5, h: b.h - 20 } }
    return { pola: [pas[s]], kalenica: null }
  }
  // dach skośny (także moduły zintegrowane): kalenica w poprzek wybranej połaci
  const polac = {
    S: { x: b.x + 8, y: cy + 5, w: b.w - 16, h: b.h / 2 - 11 },
    N: { x: b.x + 8, y: b.y + 6, w: b.w - 16, h: b.h / 2 - 11 },
    E: { x: cx + 5, y: b.y + 8, w: b.w / 2 - 11, h: b.h - 16 },
    W: { x: b.x + 6, y: b.y + 8, w: b.w / 2 - 11, h: b.h - 16 },
  }
  const pionowa = strona === 'E' || strona === 'W' || strona === 'EW'
  return {
    pola: strona === 'EW' ? [polac.W, polac.E] : [polac[strona] || polac.S],
    kalenica: pionowa ? [{ x: cx, y: b.y }, { x: cx, y: b.y + b.h }] : [{ x: b.x, y: cy }, { x: b.x + b.w, y: cy }],
  }
}

const wnetrze = (p, r) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h
const odleglosc = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

/** Najbliższy punkt na krawędzi któregoś pola modułów (stąd wychodzi trasa DC). */
export const punktPola = (pola, cel) => {
  let najlepszy = null
  for (const r of pola) {
    let p = { x: Math.min(Math.max(cel.x, r.x), r.x + r.w), y: Math.min(Math.max(cel.y, r.y), r.y + r.h) }
    if (wnetrze(cel, r)) {
      // cel pod polem (np. falownik na poddaszu): trasa schodzi z najbliższej krawędzi pola
      const krawedzie = [[cel.x - r.x, { x: r.x, y: cel.y }], [r.x + r.w - cel.x, { x: r.x + r.w, y: cel.y }],
        [cel.y - r.y, { x: cel.x, y: r.y }], [r.y + r.h - cel.y, { x: cel.x, y: r.y + r.h }]]
      p = krawedzie.sort((a, b) => a[0] - b[0])[0][1]
    }
    if (!najlepszy || odleglosc(p, cel) < odleglosc(najlepszy, cel)) najlepszy = p
  }
  return najlepszy
}

const bezPowtorzen = (punkty) => punkty.filter((p, i) => i === 0 || p.x !== punkty[i - 1].x || p.y !== punkty[i - 1].y)
/** Łamana z odcinków poziomych i pionowych: najpierw dłuższy kierunek. */
const lamana = (a, b) => bezPowtorzen(Math.abs(b.x - a.x) >= Math.abs(b.y - a.y)
  ? [a, { x: b.x, y: a.y }, b] : [a, { x: a.x, y: b.y }, b])

export const LEGENDA_ELEMENTOW = {
  F: 'falownik',
  M: 'magazyn energii',
  DC: 'rozłącznik DC',
  RG: 'rozdzielnica główna budynku',
  ZK: 'złącze z licznikiem energii',
  PWP: 'przeciwpożarowy wyłącznik prądu',
}

/**
 * Pełny opis planu: geometria (`rys`) i teksty. `dzis` tylko do testów.
 */
export const opisPlanu = ({ sprzet, stan, dzis = new Date() }) => {
  const s = { ...PUSTY_STAN, ...stan }
  const naGruncie = s.obiekt === 'grunt' || s.montaz === 'grunt'
  const stronaGruntu = ['N', 'E', 'S', 'W'].includes(s.planModuly) ? s.planModuly : 'E'
  const b = naGruncie ? UKLAD_GRUNTU[stronaGruntu].b : BUDYNEK
  const { pola, kalenica } = naGruncie ? { pola: [UKLAD_GRUNTU[stronaGruntu].p], kalenica: null }
    : polaNaBudynku(b, s.montaz === 'zintegrowany' ? 'dach_skosny' : s.montaz, s.planModuly)

  // Elementy w kolejności rozmieszczania: rozłącznik DC i magazyn po obu stronach falownika, żeby łączące je
  // odcinki nie mijały innych symboli (moduły -> rozłącznik -> falownik -> magazyn). Falownik i magazyn niosą
  // w symbolu nazwę pomieszczenia (podpis), żeby ratownik wiedział, dokąd iść.
  const przyKonstrukcji = naGruncie && s.miejsceFalownika === 'konstrukcja'
  const strefaF = przyKonstrukcji ? 'pole' : s.miejsceFalownika === 'sciana' ? 'zew' : 'wew'
  const stronaF = przyKonstrukcji ? PRZECIWNA[stronaGruntu] : s.planFalownik
  const podpisF = strefaF === 'wew' ? NAZWY_POMIESZCZEN[s.miejsceFalownika] : undefined
  const magazynOsobno = s.pomieszczenieMagazynu && s.pomieszczenieMagazynu !== 'jak_falownik'
  const elementy = []
  if (s.rozlacznikDc === 'przy_falowniku') elementy.push({ kod: 'DC', strona: stronaF, strefa: strefaF, rodzaj: 'dc' })
  elementy.push({ kod: 'F', strona: stronaF, strefa: strefaF, rodzaj: 'dc', podpis: podpisF })
  if (sprzet?.magazyn) {
    elementy.push(magazynOsobno
      ? { kod: 'M', strona: s.planMagazyn, strefa: s.pomieszczenieMagazynu === 'sciana' ? 'zew' : 'wew', rodzaj: 'dc',
        podpis: NAZWY_POMIESZCZEN[s.pomieszczenieMagazynu] }
      : { kod: 'M', strona: stronaF, strefa: strefaF, rodzaj: 'dc', podpis: podpisF })
  }
  elementy.push({ kod: 'RG', strona: s.planRozdzielnica, strefa: 'wew', rodzaj: 'ac' })
  elementy.push({ kod: 'WE', strona: s.planWejscie, strefa: 'zew', rodzaj: 'wejscie' })
  if (s.pwp === 'tak') elementy.push({ kod: 'PWP', strona: s.planPwp, strefa: 'zew', rodzaj: 'pwp' })
  elementy.push({ kod: 'ZK', strona: s.planZlacze, strefa: 'zew', rodzaj: 'ac' })

  // Rozmieszczenie: każdy element przy ścianie (w środku i na zewnątrz razem) ma własne miejsce wzdłuż ściany,
  // więc odcinek prostopadły do ściany od jednego elementu nie trafia w inny. Gdy na ścianie brakuje miejsca,
  // rozkład osobno w środku i na zewnątrz, na całą długość ściany.
  const rozmiesc = (grupa, r, odsuniecie) => {
    const pozioma = grupa[0].strona === 'N' || grupa[0].strona === 'S'
    const dlugosc = pozioma ? r.w : r.h
    const n = grupa.length
    // W środku budynku z dala od narożników: przy sąsiedniej ścianie symbole sięgają w głąb do 12,5 (ściany N, S)
    // albo 14,5 (ściany E, W). Na zewnątrz i przy polu modułów bez marginesu.
    const wSrodku = r === b && grupa.some((e) => e.strefa === 'wew')
    const margines = wSrodku ? (pozioma ? 14 : 16) : 0
    const uzytek = dlugosc - 2 * margines
    const ciasno = uzytek / (n + 1) < (pozioma ? 13 : 10) && n > 1
    if (ciasno && grupa.some((e) => e.strefa !== grupa[0].strefa)) {
      for (const strefa of ['wew', 'zew']) {
        const czesc = grupa.filter((e) => e.strefa === strefa)
        if (czesc.length) rozmiesc(czesc, r, odsuniecie)
      }
      return
    }
    grupa.forEach((e, i) => {
      const t = (margines + uzytek * (ciasno ? i / (n - 1) : (i + 1) / (n + 1))) / dlugosc
      Object.assign(e, naScianie(r, e.strona, odsuniecie(e), t), { t })
    })
  }
  const grupy = {}
  for (const e of elementy) (grupy[`${e.strefa === 'pole' ? 'pole' : 'sciana'}${e.strona}`] ||= []).push(e)
  for (const grupa of Object.values(grupy)) {
    if (grupa[0].strefa === 'pole') rozmiesc(grupa, pola[0], () => 7)
    else rozmiesc(grupa, b, (e) => (e.strefa === 'wew' ? ODSUNIECIE_WEW : ODSUNIECIE_ZEW))
  }
  const el = (kod) => elementy.find((e) => e.kod === kod)
  const F = el('F')
  const M = el('M')
  const RG = el('RG')
  const ZK = el('ZK')

  // ── Trasy ──
  // Pas przy ścianach należy do symboli: w środku do głębokości 12,5 przy ścianach N i S, 14,5 przy E i W
  // (symbol 11 x 7 leży dłuższym bokiem wzdłuż osi x). AC biegnie pierścieniem
  // 3 jednostki od ściany w środku, DC przez puste wnętrze (od głębokości 13). Element łączy się z pierścieniem
  // albo wnętrzem odcinkiem prostopadłym do swojej ściany, w swoim miejscu, więc żadna linia nie przechodzi
  // przez cudzy symbol. Ścianę poza miejscem elementu linia przecina w „wolnym miejscu” między symbolami.
  const pionowaSciana = (strona) => strona === 'E' || strona === 'W'
  const wzdluz = (p, strona) => (pionowaSciana(strona) ? p.y : p.x)
  const naBoku = (r, strona, w, glebokosc) => {
    switch (strona) {
      case 'N': return { x: w, y: r.y - glebokosc }
      case 'S': return { x: w, y: r.y + r.h + glebokosc }
      case 'W': return { x: r.x - glebokosc, y: w }
      default: return { x: r.x + r.w + glebokosc, y: w }
    }
  }
  const zajete = { N: [], E: [], S: [], W: [] }
  for (const e of elementy) if (e.strefa !== 'pole') zajete[e.strona].push(wzdluz(e, e.strona))
  /** Miejsce na ścianie najdalej od symboli (i od wcześniej użytych przejść), w zakresie [od, do]. */
  const wolneMiejsce = (strona, od, doK) => {
    const zaj = zajete[strona].filter((w) => w > od - 6 && w < doK + 6).sort((a, c) => a - c)
    const kandydaci = [od, doK, ...zaj.slice(1).map((w, i) => (w + zaj[i]) / 2),
      ...(zaj.length ? [(od + zaj[0]) / 2, (zaj.at(-1) + doK) / 2] : [(od + doK) / 2])]
    const ocena = (k) => Math.min(...zaj.map((w) => Math.abs(w - k)), Infinity)
    const najlepszy = kandydaci.filter((k) => k >= od && k <= doK).sort((a, c) => ocena(c) - ocena(a))[0]
    zajete[strona].push(najlepszy)
    return najlepszy
  }
  const gleb = (strona) => (pionowaSciana(strona) ? 16 : 14)
  const zakresWnetrza = (strona) => (pionowaSciana(strona) ? [b.y + 14, b.y + b.h - 14] : [b.x + 16, b.x + b.w - 16])
  const wnetrzeB = { x: b.x + 16, y: b.y + 14, w: b.w - 32, h: b.h - 28 }
  const pierscien = { x: b.x + 3, y: b.y + 3, w: b.w - 6, h: b.h - 6 }
  /** Punkt elementu przy ścianie przeniesiony prostopadle na głębokość `g` w środku budynku. */
  const wglab = (e, g) => naBoku(b, e.strona, wzdluz(e, e.strona), -g)

  /** Droga po pierścieniu AC między dwoma jego punktami, krótszą stroną, z narożnikami. */
  const poPierscieniu = (q1, q2) => {
    const R = pierscien
    const obwod = 2 * (R.w + R.h)
    const param = (q) => (q.y === R.y ? q.x - R.x : q.x === R.x + R.w ? R.w + q.y - R.y
      : q.y === R.y + R.h ? R.w + R.h + R.x + R.w - q.x : 2 * R.w + R.h + R.y + R.h - q.y)
    const mod = (v) => ((v % obwod) + obwod) % obwod
    const s1 = param(q1)
    const naprzod = mod(param(q2) - s1)
    const kierunek = naprzod <= obwod - naprzod ? 1 : -1
    const narozniki = [[0, { x: R.x, y: R.y }], [R.w, { x: R.x + R.w, y: R.y }], [R.w + R.h, { x: R.x + R.w, y: R.y + R.h }],
      [2 * R.w + R.h, { x: R.x, y: R.y + R.h }]]
      .map(([sn, c]) => [mod(kierunek * (sn - s1)), c])
      .filter(([d]) => d > 0 && d < (kierunek === 1 ? naprzod : obwod - naprzod))
      .sort((a, c) => a[0] - c[0])
      .map(([, c]) => c)
    return [q1, ...narozniki, q2]
  }
  /** Wejście elementu na pierścień AC. Element w polu (na gruncie) wchodzi do budynku w wolnym miejscu. */
  const doPierscienia = (e) => {
    if (e.strefa !== 'pole') {
      // element w narożniku (ciasna ściana) leży poza zasięgiem pierścienia: najpierw krótko wzdłuż ściany
      const w = wzdluz(e, e.strona)
      const [od, doK] = pionowaSciana(e.strona) ? [pierscien.y, pierscien.y + pierscien.h] : [pierscien.x, pierscien.x + pierscien.w]
      const wp = Math.min(Math.max(w, od), doK)
      const glebE = e.strefa === 'zew' ? ODSUNIECIE_ZEW : -ODSUNIECIE_WEW
      return bezPowtorzen([e, naBoku(b, e.strona, wp, e.strefa === 'zew' ? glebE : -glebE), naBoku(b, e.strona, wp, -3)])
    }
    // korytarz między symbolami budynku (na zewnątrz) a symbolami przy polu
    const sG = stronaGruntu
    const p0 = pola[0]
    const odstep = { E: p0.x - (b.x + b.w), W: b.x - (p0.x + p0.w), S: p0.y - (b.y + b.h), N: b.y - (p0.y + p0.h) }[sG]
    const pol = pionowaSciana(sG) ? 5.5 : 3.5
    const korytarz = (ODSUNIECIE_ZEW + pol + odstep - 7 - pol) / 2
    // AC dochodzi tylko do pierścienia, więc może wejść w całym jego zasięgu (nie tylko naprzeciw wnętrza)
    const [od, doK] = pionowaSciana(sG) ? [pierscien.y, pierscien.y + pierscien.h] : [pierscien.x, pierscien.x + pierscien.w]
    const w = wolneMiejsce(sG, od, doK)
    return [e, naBoku(b, sG, wzdluz(e, sG), korytarz), naBoku(b, sG, w, korytarz), naBoku(b, sG, w, -3)]
  }
  /** Droga elementu do pustego wnętrza budynku (bez przechodzenia przez cudze symbole). */
  const doWnetrzaOd = (e) => {
    if (e.strefa === 'wew') return [e, wglab(e, gleb(e.strona))]
    if (e.strefa === 'zew') {
      const [od, doK] = zakresWnetrza(e.strona)
      const w = wolneMiejsce(e.strona, od, doK)
      return [e, naBoku(b, e.strona, wzdluz(e, e.strona), 4), naBoku(b, e.strona, w, 4), naBoku(b, e.strona, w, -gleb(e.strona))]
    }
    const droga = doPierscienia(e)
    return [...droga.slice(0, -1), naBoku(b, stronaGruntu, wzdluz(droga.at(-1), stronaGruntu), -gleb(stronaGruntu))]
  }
  const trasaAc = (a, c) => {
    const pa = doPierscienia(a)
    const pc = doPierscienia(c).reverse()
    return bezPowtorzen([...pa.slice(0, -1), ...poPierscieniu(pa.at(-1), pc[0]), ...pc.slice(1)])
  }

  // DC: od modułów do rozłącznika przy falowniku (jeśli jest), dalej krótko do falownika.
  const C = s.rozlacznikDc === 'przy_falowniku' ? el('DC') : F
  let dc
  let poczatekDc
  if (przyKonstrukcji) {
    poczatekDc = naBoku(pola[0], PRZECIWNA[stronaGruntu], wzdluz(C, stronaF), 0)
    dc = [poczatekDc, C]
  } else {
    const wejscieF = [wglab(C, gleb(stronaF)), C]
    let doWnetrza
    if (naGruncie || s.montaz === 'fasada') {
      // pole poza obrysem: kabel wchodzi do budynku w wolnym miejscu ściany od strony pola
      const sciana = naGruncie ? stronaGruntu : (s.planModuly === 'EW' ? 'S' : s.planModuly)
      const [od, doK] = zakresWnetrza(sciana)
      const zakresPola = pionowaSciana(sciana) ? [pola[0].y, pola[0].y + pola[0].h] : [pola[0].x, pola[0].x + pola[0].w]
      const w = wolneMiejsce(sciana, Math.max(od, zakresPola[0] + 2), Math.min(doK, zakresPola[1] - 2))
      poczatekDc = naGruncie ? naBoku(pola[0], PRZECIWNA[sciana], w, 0) : naBoku(b, sciana, w, 4.5)
      doWnetrza = [poczatekDc, naBoku(b, sciana, w, -gleb(sciana))]
    } else {
      // moduły na dachu: z pola (część nad wnętrzem) prosto do punktu nad falownikiem
      const q = wejscieF[0]
      const kandydaci = pola.map((r) => {
        const x1 = Math.max(r.x, wnetrzeB.x)
        const y1 = Math.max(r.y, wnetrzeB.y)
        const x2 = Math.min(r.x + r.w, wnetrzeB.x + wnetrzeB.w)
        const y2 = Math.min(r.y + r.h, wnetrzeB.y + wnetrzeB.h)
        return { x: Math.min(Math.max(q.x, x1), x2), y: Math.min(Math.max(q.y, y1), y2) }
      })
      poczatekDc = kandydaci.sort((a, c) => odleglosc(a, q) - odleglosc(c, q))[0]
      doWnetrza = [poczatekDc]
    }
    const poElewacji = !naGruncie && s.montaz !== 'fasada' && s.planTrasaDc === 'elewacja'
    if (poElewacji || C.strefa === 'zew') {
      // po elewacji albo do falownika na zewnątrz: z wnętrza przez ścianę falownika w wolnym miejscu (nie przez
      // symbole w środku), dalej szyną 4 jednostki za ścianą do falownika
      const [od, doK] = zakresWnetrza(stronaF)
      const w = wolneMiejsce(stronaF, od, doK)
      const wF = wzdluz(C, stronaF)
      dc = [...doWnetrza, ...lamana(doWnetrza.at(-1), naBoku(b, stronaF, w, -gleb(stronaF))).slice(1),
        naBoku(b, stronaF, w, 4), naBoku(b, stronaF, wF, 4), C]
    } else {
      dc = [...doWnetrza, ...lamana(doWnetrza.at(-1), wejscieF[0]).slice(1), C]
    }
    dc = bezPowtorzen(dc)
  }
  if (s.rozlacznikDc === 'przy_modulach') {
    // rozłącznik przy modułach na początku trasy; na gruncie odsunięty w głąb pola, żeby nie zasłaniał falownika
    const w = naGruncie ? (() => {
      const kier = PRZECIWNA[stronaGruntu]
      return naBoku(pola[0], kier, wzdluz(poczatekDc, kier), -6)
    })() : poczatekDc
    elementy.push({ kod: 'DC', rodzaj: 'dc', ...w })
    if (w !== poczatekDc) dc = [w, ...dc]
  }
  const trasy = [{ rodzaj: 'dc', punkty: dc, przerywana: naGruncie, etykieta: naGruncie ? 'DC w ziemi' : 'DC' }]
  if (C !== F) trasy.push({ rodzaj: 'dc', punkty: [C, F], przerywana: false, etykieta: '' })
  if (M && !magazynOsobno) trasy.push({ rodzaj: 'dc', punkty: [F, M], przerywana: false, etykieta: '' })
  if (M && magazynOsobno) {
    // magazyn w innym pomieszczeniu: przewody przez wnętrze budynku
    const zF = doWnetrzaOd(F)
    const zM = doWnetrzaOd(M).reverse()
    trasy.push({ rodzaj: 'dc', punkty: bezPowtorzen([...zF, ...lamana(zF.at(-1), zM[0]).slice(1, -1), ...zM]), przerywana: false, etykieta: '' })
  }
  trasy.push({ rodzaj: 'ac', punkty: trasaAc(F, RG), przerywana: false, etykieta: 'AC' })
  trasy.push({ rodzaj: 'ac', punkty: trasaAc(RG, ZK), przerywana: false, etykieta: '' })

  // Wejście główne jako strzałka do ściany zamiast prostokąta.
  const WE = el('WE')
  const wejscie = { od: { x: WE.x, y: WE.y }, do: naScianie(b, WE.strona, 1, WE.t) }

  const symbole = elementy.filter((e) => e.rodzaj !== 'wejscie').map(({ kod, x, y, rodzaj, podpis }) => ({ kod, x, y, rodzaj, podpis }))

  // Teksty
  const brakOdlaczaniaPrzyModulach = !s.odlaczanieModulow || s.odlaczanieModulow === 'brak'
  const ostrzezenia = []
  if (brakOdlaczaniaPrzyModulach) {
    ostrzezenia.push('Przewody DC od modułów do falownika (linia czerwona) są pod napięciem przy oświetlonych modułach, także po wyłączeniu zasilania budynku.')
  }
  if (sprzet?.magazyn) ostrzezenia.push('Magazyn energii i jego przewody są pod napięciem także po odłączeniu sieci i modułów.')
  if (s.agregat === 'tak') ostrzezenia.push('W obiekcie jest agregat prądotwórczy.')

  const m = sprzet?.moduly
  const f = sprzet?.falownik
  const g = sprzet?.magazyn
  const falownikGdzie = polozenieFalownika(s)
  const dane = [
    ['Adres obiektu', s.adresInstalacji],
    ['Moc modułów', m?.sumaKw ? `${liczbaPl(m.sumaKw)} kWp` : ''],
    ['Moduły', m?.liczba ? `${m.liczba} szt.${[m.producent, m.model].filter(Boolean).length ? ` ${[m.producent, m.model].filter(Boolean).join(' ')}` : ''}` : ''],
    ['Falownik', [[f?.producent, f?.model].filter(Boolean).join(' '), falownikGdzie].filter(Boolean).join(', ')],
    ...(g ? [['Magazyn energii', [[g.producent, g.model].filter(Boolean).join(' '), g.kwhRazem ? `${liczbaPl(g.kwhRazem)} kWh` : '', polozenieMagazynu(s)].filter(Boolean).join(', ')]] : []),
    ['Zasilanie rezerwowe', zdanieZasilaniaRezerwowego(sprzet, s)],
  ]

  const legenda = [
    ...['F', 'DC', 'M', 'RG', 'PWP', 'ZK'].filter((k) => symbole.some((e) => e.kod === k)).map((k) => ({ typ: 'symbol', kod: k, opis: LEGENDA_ELEMENTOW[k] })),
    { typ: 'pole', opis: naGruncie ? 'moduły fotowoltaiczne na konstrukcji gruntowej' : 'obszar modułów fotowoltaicznych' },
    { typ: 'dc', opis: `przewody DC${s.planOslony === 'tak' ? ' (w osłonie ognioodpornej)' : ''}${naGruncie ? ', kabel w ziemi linią przerywaną' : ''}` },
    { typ: 'ac', opis: 'przewody AC' },
    { typ: 'wejscie', opis: 'wejście główne do budynku' },
  ]

  return {
    tytul: 'Plan urządzenia fotowoltaicznego dla ekip ratowniczych',
    podtytul: 'Rysunek schematyczny bez skali. Góra rysunku to północ.',
    rys: { budynek: b, kalenica, pola, symbole, trasy, wejscie, naGruncie },
    dane,
    // zdanie o napięciu DC stoi w czerwonej ramce ostrzeżeń, więc tu go nie powtarzamy
    odlaczenie: zdanieOdlaczenia(s).replace(ZDANIE_DC_POD_NAPIECIEM, '').trim(),
    ostrzezenia,
    legenda,
    oslony: s.planOslony === 'tak',
    autor: s.planAutor.trim(),
    data: dataPl(s.planData) || dataPl(dzis.toISOString().slice(0, 10)),
    dopisek: 'Załącznik do zawiadomienia o zakończeniu instalowania urządzeń fotowoltaicznych '
      + '(art. 29 ust. 4 pkt 3 lit. c Prawa budowlanego).',
  }
}
